import {
  Checkbox as MUICheckbox,
  CheckboxProps as MUICheckboxProps,
  Table as MUITable,
  TableBody as MUITableBody,
  TableBodyProps as MUITableBodyProps,
  TableContainer as MUITableContainer,
  TableContainerProps as MUITableContainerProps,
  TableRow as MUITableRow,
  TableRowProps as MUITableRowProps,
  Stack,
  SxProps,
  TableCell,
  TableCellProps,
  Typography,
} from "@mui/material";
import { Property } from "csstype";
import { ReactNode } from "react";
import { UAParser } from "ua-parser-js";
import { PrintPage, PrintPageBody, V2ExamineeInfo } from "../../commons";

/**
 * {@link TableHeaderCell} のプロパティ
 */
export interface TableHeaderCellProps {
  /** 要素 */
  children: ReactNode;
  /** 横幅 */
  width?: Property.Width;
  /** 高さ */
  height?: Property.Height;
  /** 最初のセルか否か */
  isFirstCell?: boolean;
}
/**
 * 医療機関向けレポートヘッダー用の TableCell
 * @param param0 {@link TableHeaderCellProps}
 * @returns 医療機関向けレポートヘッダー用の TableCell
 */
export function TableHeaderCell({
  children,
  width,
  height = "36px",
  isFirstCell = false,
}: TableHeaderCellProps) {
  return (
    <TableCell
      component="th"
      sx={{
        "&.MuiTableCell-root": {
          color: "#000000",
          fontFeatureSettings: "'clig' off, 'liga' off",
          fontFamily: "Noto Sans JP",
          fontSize: "10px",
          fontStyle: "normal",
          fontWeight: 500,
          lineHeight: "150%",
          letterSpacing: "0.2px",
          borderBottom: "1px solid #000",
          p: 0,
          height,
          width,
        },
      }}
    >
      <Stack
        component="span"
        sx={{
          pl: "14px",
          borderLeft: `${isFirstCell ? "0" : "1px"} solid #000`,
        }}
      >
        {children}
      </Stack>
    </TableCell>
  );
}

/**
 * 疾患リスク設問と回答
 */
export interface Question {
  /** 設問 */
  question: ReactNode;
  /** 回答 */
  choices: {
    /** テキスト */
    text: string;
    /** 回答に対するポイント */
    point: number;
  }[];
}
export interface QuestionRowsProps {
  /** {@link Question} */
  question: Question;
  /** 選択項目 */
  choiceIndex: number | null;
  /** 行番号 */
  rowIndex: number;
  /** 行の高さ */
  rowHeight?: Property.Height;
  /** 選択肢の {@link SxProps} */
  choiceStyle: SxProps;
  /** 点数の {@link SxProps} */
  pointStyle: SxProps;
}
/**
 * 疾患リスク設問の行
 */
export function QuestionRows({
  question,
  choiceIndex,
  rowIndex,
  rowHeight,
  choiceStyle,
  pointStyle,
}: QuestionRowsProps) {
  const rowProps: SxProps = [
    {
      "&.MuiTableRow-root td": {
        border: "solid #000",
        borderWidth: "0 1px 1px 0 !important",
      },
    },
    {
      "&.MuiTableRow-root td": {
        backgroundColor: "rgba(234, 234, 234, 0.30)",
        border: "solid #000",
        borderWidth: "0 1px 1px 0 !important",
      },
    },
  ][rowIndex % 2];
  return (
    <>
      {question.choices.map((c, i) => (
        <TableRow
          key={i}
          sx={{
            height: rowHeight,
            ...rowProps,
          }}
        >
          {i === 0 && (
            <QuestionCell rowSpan={question.choices.length}>
              {question.question}
            </QuestionCell>
          )}
          <AnswerCell>
            <Checkbox checked={choiceIndex === i}></Checkbox>
          </AnswerCell>
          <ChoicesCell sx={choiceStyle}>{c.text}</ChoicesCell>
          <PointCell sx={pointStyle}>{c.point}</PointCell>
        </TableRow>
      ))}
    </>
  );
}

/**
 * {@link PrintPageHeader} のプロパティ
 */
export interface PrintPageHeaderProps {
  /** ヘッダーのタイトル1 */
  title1: string;
  /** ヘッダーのタイトル2 */
  title2?: string;
  /** ヘッダーのサブタイトル */
  subtitle?: string;
}
/**
 * 疾患リスクレポート用のヘッダー
 *
 * @param param0 {@link PrintPageHeader}
 * @returns 疾患リスクレポート用のヘッダー
 */
export function PrintPageHeader({
  title1,
  title2,
  subtitle,
}: PrintPageHeaderProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        bgcolor: "#01996D",
        p: "24px",
        height: "26px",
      }}
    >
      <Stack direction="row" alignItems="baseline">
        <Typography
          sx={{
            color: "#FFFFFF",
            fontFeatureSettings: "'palt' on, 'clig' off, 'liga' off",
            fontSize: "26px",
            fontStyle: "normal",
            fontWeight: "700",
            letterSpacing: "0.4px",
            lineHeight: "100%",
          }}
        >
          {title1}
          {title2 && `│${title2}`}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              color: "#FFFFFF",
              fontFeatureSettings: "'palt' on, 'clig' off, 'liga' off",
              fontSize: "10px",
              fontStyle: "normal",
              fontWeight: "700",
              letterSpacing: "0.4px",
              lineHeight: "100%",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Stack>

      <img
        src="/img/sleep_compass_logo.svg"
        alt="SLEEP COMPASS"
        loading="lazy"
        style={{ width: "88.4px", height: "26px" }}
      />
    </Stack>
  );
}

/**
 * {@link MedicalFacilityReportLayout} 用のプロパティ
 */
export interface MedicalFacilityReportLayoutProps {
  title: string;
  subTitle: string;
  /** 受診者情報 */
  examinee: V2ExamineeInfo;
  /** ページ番号 */
  pageNumber: number;
  /** 表示要素 */
  children: ReactNode;
  /** セクションタイトルとコンテンツ間の gap */
  contentsGap?: Property.Gap;
}
/**
 * 医療機関用レポートのレイアウト
 *
 * @param param0 {@link MedicalFacilityReportLayoutProps}
 * @returns 医療機関用レポートのレイアウト
 */
export function MedicalFacilityReportLayout({
  title,
  subTitle,
  examinee,
  pageNumber,
  children,
  contentsGap = "24px",
}: MedicalFacilityReportLayoutProps) {
  return (
    <PrintablePageLayout
      title={title}
      subTitle={subTitle}
      pageNumber={pageNumber}
    >
      <Stack sx={{ gap: contentsGap }}>
        <SectionTitle text="受診者情報" />
        <ExamineeInfoSection examinee={examinee} />
      </Stack>
      {children}
    </PrintablePageLayout>
  );
}

export interface PrintablePageLayoutLayoutProps {
  title: string;
  subTitle: string;
  /** ページ番号 */
  pageNumber: number;
  /** 表示要素 */
  children: ReactNode;
}
export function PrintablePageLayout({
  title,
  subTitle,
  pageNumber,
  children,
}: PrintablePageLayoutLayoutProps) {
  // 表示が崩れるので特定の OS とブラウザの組み合わせに対してページ右下の番号を表示しない
  const parser = new UAParser(window.navigator.userAgent);
  const parserResults = parser.getResult();
  const osName = parserResults.os.name;
  const browserName = parserResults.browser.name;
  const isPageNumberDisable =
    [
      // https://www.notion.so/accelstars/iPhone-safari-PDF-2565f666810580bcae22ff3594007137
      osName?.includes("iOS") && browserName?.includes("Safari"),
    ].some((comb) => comb === true) || false;
  return (
    <PrintPage>
      <PrintPageHeader
        title1="医療機関用レポート"
        title2={title}
        subtitle={subTitle}
      />
      <PrintPageBody>{children}</PrintPageBody>
      {!isPageNumberDisable && (
        <Typography
          component="span"
          sx={{
            position: "absolute",
            bottom: "10px",
            right: "18px",
            fontSize: "8px",
            fontWeight: 700,
          }}
        >
          {pageNumber}
        </Typography>
      )}
    </PrintPage>
  );
}

/**
 * {@link InterviewTableBody} のプロパティ
 */
export type InterviewTableBodyProps = MUITableBodyProps;
/**
 * 医療機関用レポートの問診用 TableBody
 * @param props
 * @returns 医療機関用レポートの問診用 TableBody
 */
export function InterviewTableBody({ sx, ...props }: InterviewTableBodyProps) {
  return (
    <MUITableBody
      sx={{
        "& td:not([rowspan]):not([colspan])": {
          borderLeft: "1px solid #E0E0E0",
        },
        ...sx,
      }}
      {...props}
    />
  );
}

/**
 * レポート用テーブルコンテナ
 * @param param0 {@link MUITableContainerProps MUI用のTableContainerProps}
 * @returns レポート用テーブルコンテナ
 */
export function TableContainer({ sx, ...props }: MUITableContainerProps) {
  return (
    <MUITableContainer
      sx={{
        border: "1px solid #E0E0E0",
        td: {
          borderBottom: 0,
        },
        "& tr:not(:last-child)": {
          borderBottom: "1px solid #E0E0E0",
        },
        "& tr th": {
          backgroundColor: "#F4F4F4",
          fontWeight: "700",
          fontSize: "10px",
          p: "11px 11px 11px 16px",
        },
        ...sx,
      }}
      {...props}
    ></MUITableContainer>
  );
}

/**
 * レポート用TableRow
 * @param param0 {@link MUITableRowProps MUI用のTableRow}
 * @returns レポート用TableRow
 */
export function TableRow({ sx, ...props }: MUITableRowProps) {
  return (
    <MUITableRow
      sx={{
        "&>th.MuiTableCell-root": {
          background: "rgba(234, 234, 234, 0.50)",
        },
        ...sx,
      }}
      {...props}
    />
  );
}
/**
 * {@link Checkbox} 用のプロパティ
 */
export type CheckboxProps = Omit<MUICheckboxProps, "disabled">;
/**
 * レポート用のチェックボックス
 */
export function Checkbox({ checked, ...props }: CheckboxProps) {
  return (
    <MUICheckbox
      {...props}
      sx={{
        width: "17px",
        height: "17px",
        "&.Mui-checked": {
          color: "#01996D",
        },
        "&.MuiCheckbox-root": {
          padding: "0px",
        },
        transform: "scale(0.7)",
      }}
      readOnly
      checked={checked}
      disabled={!checked}
    />
  );
}
/**
 * {@link QuestionCell} 用のプロパティ
 */
export interface QuestionCellProps {
  /** 要素 */
  children?: ReactNode;
  /** TableCell の rowSpan */
  rowSpan: TableCellProps["rowSpan"];
}
/**
 * 質問用のTableCell
 * @param param0 {@link QuestionCellProps}
 * @returns 質問用のTableCell
 */
export function QuestionCell({ children, rowSpan }: QuestionCellProps) {
  return (
    <TableCell
      rowSpan={rowSpan}
      sx={{
        verticalAlign: "top",
        p: "8px 12px",
      }}
    >
      {children}
    </TableCell>
  );
}
/**
 * {@link PointCell} 用のプロパティ
 */
export interface PointCellProps {
  /** 要素 */
  children: ReactNode;
  /** ポイント用のTableCellの{@link SxProps} */
  sx: SxProps;
}
/**
 * ポイント用のTableCell
 *
 * @param param0 {@link PointCellProps}
 * @returns ポイント用のTableCell
 */
export function PointCell({ children, sx }: PointCellProps) {
  return (
    <TableCell
      sx={{
        p: "0px",
        textAlign: "center",
      }}
    >
      <Typography sx={sx}>{children}</Typography>
    </TableCell>
  );
}
/**
 * {@link ChoicesCell} 用のプロパティ
 */
export interface ChoicesCellProps {
  /** 要素 */
  children: ReactNode;
  /** {@link SxProps} プロパティ */
  sx: SxProps;
}
/**
 * 選択肢用の TableCell
 * @param param0 {@link ChoicesCellProps}
 * @returns 選択肢用の TableCell
 */
export function ChoicesCell({ children, sx }: ChoicesCellProps) {
  return (
    <TableCell
      sx={{
        textAlign: "left",
        p: "0px 16px",
      }}
    >
      <Typography sx={sx}>{children}</Typography>
    </TableCell>
  );
}
/**
 * {@link AnswerCell} 用のTableCell
 */
export interface AnswerCellProps {
  /** 要素 */
  children: ReactNode;
}
/**
 * 回答用のTableCell
 * @param param0 {@link AnswerCellProps}
 * @returns 回答用のTableCell
 */
export function AnswerCell({ children }: AnswerCellProps) {
  return (
    <TableCell
      sx={{
        textAlign: "center",
        p: "0px",
        justifyContent: "center",
        lineHeight: "100%",
      }}
    >
      {children}
    </TableCell>
  );
}
/**
 * {@link TableQuestionTypography} 用のプロパティ
 */
export interface TableQuestionTypographyProps {
  /** 要素 */
  children: ReactNode;
}
/**
 * 設問用の文字装飾
 */
export function TableQuestionTypography({
  children,
}: TableQuestionTypographyProps) {
  return (
    <Typography
      sx={{
        color: "#000000",
        fontFeatureSettings: " 'clig' off, 'liga' off",
        fontFamily: "Noto Sans JP",
        fontSize: "12px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "150%",
        letterSpacing: "0.2px",
      }}
    >
      {children}
    </Typography>
  );
}
/**
 * {@link BasisOfDetermination} のプロパティ
 */
export interface BasisOfDeterminationProps {
  /**
   * 根拠の注釈
   */
  basisAnnotations: string[];
}
/**
 * 判定の根拠の注釈
 * @param props
 */
export function BasisOfDetermination({
  basisAnnotations,
}: BasisOfDeterminationProps) {
  return (
    <table>
      <tbody>
        {basisAnnotations.map((annotation, i) => (
          <tr key={i}>
            {i === 0 && (
              <td
                rowSpan={basisAnnotations.length}
                style={{ verticalAlign: "top" }}
              >
                <Typography
                  sx={{
                    color: "#707070",
                    textAlign: "justify",
                    fontFeatureSettings: "'clig' off, 'liga' off",
                    fontFamily: "Noto Sans JP",
                    fontSize: "8px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "170%",
                    letterSpacing: "0.15px",
                  }}
                >
                  判定の根拠：
                </Typography>
              </td>
            )}
            <td>
              <Typography
                sx={{
                  color: "#707070",
                  textAlign: "justify",
                  fontFeatureSettings: "'clig' off, 'liga' off",
                  fontFamily: "Noto Sans JP",
                  fontSize: "8px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "170%",
                  letterSpacing: "0.15px",
                }}
              >
                {annotation}
              </Typography>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * {@link ExamineeInfoSection} のプロパティ
 */
export interface ExamineeInfoSectionProps {
  /** 受診者情報 */
  examinee: V2ExamineeInfo;
}
/**
 * 受診者情報
 * @param param0 {@link V2ExamineeInfo}
 * @returns 受診者情報
 */
export function ExamineeInfoSection({ examinee }: ExamineeInfoSectionProps) {
  const cellStyle = {
    border: "solid #000",
    borderWidth: "0px 1px 1px 0px !important",
    fontSize: "10px",
    fontWeight: "400",
    height: "36px",
    p: 0,
    pl: "8px",
    width: "calc(100% / 3 - 60px)",
  };
  const titleCellStyle = {
    ...cellStyle,
    fontWeight: "500",
    width: "52px",
    backgroundColor: "#EAEAEA80",
  };
  return (
    <TableContainer
      sx={{
        ml: "8px",
        border: "solid #000",
        borderWidth: "1px 0px 0px 1px",
        borderRadius: 0,
      }}
    >
      <MUITable sx={{ minWidth: "730px", tableLayout: "fixed" }}>
        <MUITableBody>
          <MUITableRow>
            <TableCell sx={titleCellStyle}>氏名</TableCell>
            <TableCell sx={cellStyle}>{examinee.name}</TableCell>
            <TableCell sx={titleCellStyle}>性別</TableCell>
            <TableCell sx={cellStyle}>{examinee.gender || "-"}</TableCell>
            <TableCell sx={titleCellStyle}>生年月日</TableCell>
            <TableCell sx={cellStyle}>{examinee.birthday}</TableCell>
          </MUITableRow>
          <TableRow>
            <TableCell sx={titleCellStyle}>実施機関</TableCell>
            <TableCell colSpan={5} sx={cellStyle}>
              {examinee.facility_name}
            </TableCell>
          </TableRow>
        </MUITableBody>
      </MUITable>
    </TableContainer>
  );
}

export function getMeasurementTermString(
  measurementPeriodStart: string,
  measurementPeriodEnd: string
) {
  const measurementPeriodStartDate = new Date(measurementPeriodStart);
  const measurementPeriodStartDateString =
    measurementPeriodStartDate.getFullYear() +
    "年" +
    (measurementPeriodStartDate.getMonth() + 1) +
    "月" +
    measurementPeriodStartDate.getDate() +
    "日";

  const measurementPeriodEndDate = new Date(measurementPeriodEnd);
  const measurementPeriodEndDateString =
    measurementPeriodEndDate.getFullYear() ===
    measurementPeriodStartDate.getFullYear()
      ? measurementPeriodEndDate.getMonth() +
        1 +
        "月" +
        measurementPeriodEndDate.getDate() +
        "日"
      : measurementPeriodEndDate.getFullYear() +
        "年" +
        (measurementPeriodEndDate.getMonth() + 1) +
        "月" +
        measurementPeriodEndDate.getDate() +
        "日";

  return `${measurementPeriodStartDateString}〜${measurementPeriodEndDateString}`;
}

/**
 * {@link SectionTitle} のプロパティ
 */
export interface SectionTitleProps {
  /** テキスト */
  text: string;
  /** サブテキスト */
  subtext?: string;
  /** セクションの色 */
  color?: string;
}
/**
 * セクションタイトル
 *
 * @param param0 {@link SectionTitleProps}
 * @returns セクションタイトル
 */
export function SectionTitle({
  text,
  subtext,
  color = "#01996D",
}: SectionTitleProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        borderLeft: `5px solid ${color}`,
        p: "0 11px",
        gap: "11px",
      }}
    >
      <Typography
        sx={{
          color: "#000000",
          fontFeatureSettings: "'clig' off, 'liga' off",
          fontSize: "16px",
          fontWeight: 500,
          lineHeight: "100%",
          letterSpacing: "0.2px",
          "& sub": {
            color: "#707070",
            fontSize: "8px",
            fontWeight: 500,
            letterSpacing: "0.2px",
            ml: "4px",
            verticalAlign: "baseline",
          },
        }}
      >
        {text}
        {subtext && <sub>{subtext}</sub>}
      </Typography>
    </Stack>
  );
}
